import { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { rankName } from '@/utils/rankName';

interface LevelUpOverlayProps {
  visible: boolean;
  newLevel: number;
  levelName: string;
  onDismiss: () => void;
}

function Confetti() {
  const particles = useMemo(() => {
    const colors = ['#2D6A4F', '#C49F38', '#5B8BA0', '#7C5CBF', '#E07A3A', '#D16BA5'];
    return Array.from({ length: 28 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      x: Math.random() * 100,
      delay: Math.random() * 0.6,
      size: 4 + Math.random() * 6,
      rotation: Math.random() * 360,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-sm"
          style={{
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            left: `${p.x}%`,
            top: '-5%',
          }}
          initial={{ y: 0, rotate: 0, opacity: 1 }}
          animate={{
            y: '120vh',
            rotate: p.rotation + 720,
            opacity: [1, 1, 0.8, 0],
          }}
          transition={{
            duration: 2.5 + Math.random(),
            delay: p.delay,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        />
      ))}
    </div>
  );
}

export default function LevelUpOverlay({ visible, newLevel, levelName, onDismiss }: LevelUpOverlayProps) {
  const { t } = useTranslation('scout');

  useEffect(() => {
    if (visible) {
      navigator.vibrate?.(100);
      const timer = setTimeout(onDismiss, 3000);
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onDismiss();
      };
      document.addEventListener('keydown', handleKey);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKey);
      };
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onDismiss}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <Confetti />

          {/* Content */}
          <motion.div
            className="relative flex flex-col items-center z-10 px-8"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.1 }}
          >
            <motion.p
              className="text-4xl font-black text-white mb-2"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {t('celebrations.levelUp')}
            </motion.p>

            <motion.div
              className="w-24 h-24 rounded-full bg-gradient-to-br from-gold to-gold/70 flex items-center justify-center shadow-2xl mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10, stiffness: 180, delay: 0.3 }}
            >
              <span className="text-4xl font-black text-white">{newLevel}</span>
            </motion.div>

            <motion.p
              className="text-section font-bold text-white/90 mb-1"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {rankName(newLevel, levelName)}
            </motion.p>

            <motion.p
              className="text-small text-white/50 mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {t('celebrations.tapToDismiss')}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
